import React, { Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate } from 'react-router-dom'

import {
    Avatar,
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    List,
    ListItem,
    Stack,
    Typography,
    Chip,
} from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'

import {
    Close as CloseIcon,
    Menu as MenuIcon,
    CloudOff as CloudOffIcon,
    AccountCircleOutlined as AccountCircleOutlinedIcon,
    Logout as LogoutIcon,
} from '@mui/icons-material'

import logoImg from '@/assets/img/logo.png'

import { useAppDispatch, useAppSelector } from '@/redux/rootReducer'
import { logout, selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useOfflineMode } from '@/providers/OfflineModeProvider'

import { offlineSidebarItems } from './offlineSidebarItems'

const HeaderIconButton = styled(IconButton)(({ theme }) => ({
    width: '40px',
    height: '40px',
    background: theme.palette.primary.dark,
    border: `1px solid ${theme.palette.primary.light}`,
    color: theme.palette.background.default,
    '&:hover': {
        background: theme.palette.primary.light,
    },
}))

export function OfflineMobileSidebar() {
    const [open, setOpen] = useState(false)
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { pathname } = useLocation()
    const authUser = useAppSelector(selectAuthUser)
    const { isOfflineMode, timeUntilExpiry } = useOfflineMode()

    const handleOpen = () => setOpen(true)
    const handleClose = () => setOpen(false)

    const handleLogout = () => {
        dispatch(logout())
        handleClose()
    }

    const handleClick = (link: string) => {
        navigate(link)
        handleClose()
    }

    const formatTimeRemaining = (hours: number) => {
        const h = Math.floor(hours)
        const m = Math.floor((hours % 1) * 60)
        return `${h}h ${m}m`
    }

    return (
        <Box>
            <IconButton onClick={handleOpen} color="inherit">
                <MenuIcon />
            </IconButton>
            <Dialog fullScreen open={open} onClose={handleClose}>
                <DialogTitle>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Button component={Link} to="/offline/patients" onClick={() => handleClose()}>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                    <img src={authUser?.establishment?.logo?.url ?? logoImg} alt="Logo" height="40" />
                                    <CloudOffIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                </Stack>
                            </Button>
                        </Box>
                        <IconButton edge="end" color="inherit" onClick={handleClose} aria-label="close">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {/* Offline Status */}
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ mb: 1 }}>
                            <CloudOffIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                                {t('Offline Mode')}
                            </Typography>
                        </Stack>
                        {timeUntilExpiry && timeUntilExpiry > 0 && (
                            <Chip
                                label={`${t('Expires in')} ${formatTimeRemaining(timeUntilExpiry)}`}
                                size="small"
                                color="warning"
                                variant="outlined"
                            />
                        )}
                    </Box>

                    {/* Navigation Items */}
                    {offlineSidebarItems.map((item) => {
                        const isMenuItemActive = item.isPathBase ? item.link === pathname : pathname.startsWith(item.link)

                        return (
                            <Fragment key={item.label}>
                                <ListItem onClick={() => handleClick(item.link)}>
                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        sx={{
                                            fontWeight: isMenuItemActive ? 'bold' : '400',
                                            fontSize: '1rem',
                                            fontFamily: '"Inter","Roboto","Helvetica","Arial",sans-serif',
                                        }}
                                    >
                                        {t(item.label)}
                                    </Typography>
                                </ListItem>
                            </Fragment>
                        )
                    })}

                    {/* User Profile Section */}
                    <Box mt={2} borderTop={1} borderColor="divider">
                        <Box display="flex" alignItems="center" py={2}>
                            <Avatar
                                sx={{
                                    width: 40,
                                    height: 40,
                                    background: 'radial-gradient(circle, rgba(42,182,183,1) 0%, rgba(37,154,164,1) 100%)'
                                }}
                            >
                                <AccountCircleOutlinedIcon color="primary" />
                            </Avatar>
                            <Box ml={2}>
                                <Typography variant="subtitle1">
                                    {authUser?.userInfo.name + ' ' + authUser?.userInfo.surname}
                                </Typography>
                                <Typography variant="subtitle2" color="text.secondary">
                                    {authUser?.email}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Actions */}
                        <List>
                            <ListItem button onClick={() => handleClick('/patients')}>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        fontWeight: '400',
                                        fontSize: '1rem',
                                        fontFamily: '"Inter","Roboto","Helvetica","Arial",sans-serif',
                                    }}
                                >
                                    {t('Return to Online Mode')}
                                </Typography>
                            </ListItem>
                            <ListItem button onClick={handleLogout}>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        fontWeight: '400',
                                        fontSize: '1rem',
                                        fontFamily: '"Inter","Roboto","Helvetica","Arial",sans-serif',
                                    }}
                                >
                                    {t('Logout')}
                                </Typography>
                            </ListItem>
                        </List>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    )
}

export default OfflineMobileSidebar 